import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Hammer, ChevronRight, FileSignature, Loader2 } from "lucide-react";
import { toast } from "sonner";

function TemplateGrid({ templates, onSelect }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="template-grid">
      {templates.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onSelect(t.id)}
          data-testid={`template-card-${t.id}`}
          className="card-sharp p-6 text-left hover:gold-glow transition-all duration-300 group"
        >
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <div className="label-tag">{t.category}</div>
              <h3 className="text-xl font-bold tracking-tight">{t.name}</h3>
              <div className="font-mono text-[10px] gold-text">{t.code}</div>
            </div>
            <div className="w-10 h-10 flex items-center justify-center border border-[#C9A227]/40 group-hover:bg-[#C9A227]/10 transition-colors">
              <ChevronRight className="w-5 h-5 gold-text" strokeWidth={1.5} />
            </div>
          </div>
          <p className="text-sm text-gray-400 mt-4 leading-relaxed">{t.description}</p>
          <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between text-xs">
            <span className="label-tag">{t.variable_count} fields</span>
            <span className="gold-text font-bold uppercase tracking-widest text-[10px]">
              Forge →
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}

function VariableField({ variable, value, admirals, onChange }) {
  const base =
    "w-full bg-black/50 border border-white/10 text-white placeholder:text-gray-600 focus:border-[#C9A227] focus:outline-none focus:ring-1 focus:ring-[#C9A227] transition-all px-4 py-3 text-sm rounded-none";

  if (variable.type === "admiral_select") {
    return (
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={base}
        data-testid={`field-${variable.key}`}
        required={variable.required}
      >
        <option value="">Select a signatory…</option>
        {admirals.map((a) => (
          <option key={a.id} value={a.admiral_name}>
            {a.admiral_name} · {a.title || a.rank}
            {a.vessel_id ? ` · ${a.vessel_id}` : ""}
          </option>
        ))}
      </select>
    );
  }
  if (variable.type === "textarea") {
    return (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`${base} min-h-[110px] resize-y`}
        placeholder={variable.label}
        data-testid={`field-${variable.key}`}
        required={variable.required}
      />
    );
  }
  if (variable.type === "number") {
    return (
      <input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className={base}
        placeholder={variable.label}
        data-testid={`field-${variable.key}`}
        required={variable.required}
      />
    );
  }
  if (variable.type === "date") {
    return (
      <input
        type="date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={base}
        data-testid={`field-${variable.key}`}
        required={variable.required}
      />
    );
  }
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className={base}
      placeholder={variable.label}
      data-testid={`field-${variable.key}`}
      required={variable.required}
    />
  );
}

function ForgeForm({ template, admirals, onForged, onBack }) {
  const initialData = useMemo(() => {
    const obj = {};
    template.variables.forEach((v) => {
      if (v.default !== undefined) obj[v.key] = v.default;
    });
    // Default effective date to today
    if (template.variables.find((v) => v.key === "effective_date")) {
      obj.effective_date = new Date().toISOString().slice(0, 10);
    }
    return obj;
  }, [template]);

  const [data, setData] = useState(initialData);
  const [forging, setForging] = useState(false);

  // Auto-fill from Admiral selection
  const handleChange = (key, value) => {
    setData((prev) => {
      const next = { ...prev, [key]: value };
      const isAdmiralKey = ["admiral_name", "recipient_name", "guardian_witness"].includes(key);
      if (isAdmiralKey && value) {
        const a = admirals.find((x) => x.admiral_name === value);
        if (a) {
          if (!next.vessel_id && a.vessel_id) next.vessel_id = a.vessel_id;
          if (!next.gold_amount && a.gold_amount) next.gold_amount = a.gold_amount;
        }
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setForging(true);
    try {
      const contract = await api.forgeContract(template.id, data);
      toast.success("Contract forged into the Imperial Archives", {
        description: contract.contract_code,
      });
      onForged(contract);
    } catch (err) {
      toast.error("Forge failed", {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setForging(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8" data-testid="forge-form">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <button
            type="button"
            onClick={onBack}
            className="text-xs label-tag hover:text-white transition-colors mb-3"
            data-testid="forge-back-btn"
          >
            ← Choose Different Template
          </button>
          <div className="label-tag">{template.category}</div>
          <h2 className="text-3xl font-black tracking-tighter mt-1">{template.name}</h2>
          <p className="text-gray-400 text-sm mt-2 max-w-2xl">{template.description}</p>
          <div className="font-mono text-[10px] gold-text mt-2">{template.code}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {template.variables.map((v) => (
          <div key={v.key} className="space-y-2">
            <label className="label-tag flex items-center gap-2">
              {v.label}
              {v.required && <span className="text-[#C9A227]">·</span>}
            </label>
            <VariableField
              variable={v}
              value={data[v.key]}
              admirals={admirals}
              onChange={(val) => handleChange(v.key, val)}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 pt-4 border-t border-white/10">
        <button
          type="submit"
          disabled={forging}
          className="btn-gold inline-flex items-center gap-2"
          data-testid="forge-submit-btn"
        >
          {forging ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />}
          {forging ? "Forging…" : "Forge Contract"}
        </button>
        <span className="text-xs text-gray-500">
          Saved to Imperial Archives · ready for Digital Handshake
        </span>
      </div>
    </form>
  );
}

export default function Forge() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [admirals, setAdmirals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.getTemplates(), api.getAdmirals()])
      .then(([t, a]) => {
        setTemplates(t);
        setAdmirals(a);
      })
      .catch((err) => toast.error("Failed to load Forge", { description: err.message }))
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = async (id) => {
    try {
      const tpl = await api.getTemplate(id);
      setSelected(tpl);
    } catch (err) {
      toast.error("Failed to load template", { description: err.message });
    }
  };

  const handleForged = (contract) => {
    navigate(`/archives/${contract.id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-500" data-testid="forge-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-3" /> Igniting the Forge…
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="forge-page">
      <section>
        <div className="flex items-center gap-3 mb-3">
          <Hammer className="w-5 h-5 gold-text" strokeWidth={1.5} />
          <span className="label-tag">Contract Template Forge</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">
          Strike the <span className="gold-text">Imperial Seal</span>.
        </h1>
        <p className="text-gray-400 mt-3 max-w-2xl">
          Select a sovereign template, inject live Ledger data, and forge a binding instrument
          ready for the Digital Handshake.
        </p>
      </section>

      <div className="divider-gold" />

      {!selected ? (
        <TemplateGrid templates={templates} onSelect={handleSelect} />
      ) : (
        <ForgeForm
          template={selected}
          admirals={admirals}
          onBack={() => setSelected(null)}
          onForged={handleForged}
        />
      )}
    </div>
  );
}
